import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material';
import { BLOCKED_EMAIL_DOMAINS } from '../../../../shared/constants/blockedEmailDomains';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { countryCodes } from '../../../../shared/constants/countryCodes';
import { useSurveyContent } from '../../../../shared/hooks';
import { useHandleSubmit } from './hooks';
import AlertComp from '../../../_shared/alert';
import { ROUTES } from 'shared/routes/constants';
import { Link } from 'react-router-dom';

export const ContactInformationForm = (): JSX.Element => {
  const { surveyContent } = useSurveyContent();
  const handleSubmit = useHandleSubmit();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [showPreferredEmailRejected, setShowPreferredEmailRejected] = useState(false);
  const [finishedPosting, setFinishedPosting] = useState(false);
  const [email, setEmail] = useState('');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  const phoneInputWrapperRef = React.useRef<HTMLDivElement | null>(null);

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  const emailDomain = email.split('@')[1] || '';
  const consultationMethod = surveyContent?.consultation_method;
  const isBlockedDomain =
    BLOCKED_EMAIL_DOMAINS.includes(emailDomain) || emailDomain.endsWith('.edu');

  useEffect(() => {
    if (consultationMethod === 'ai_moderated') {
      setFinishedPosting(true);
    }
  }, [consultationMethod]);

  useEffect(() => {
    if (finishedPosting) {
      const urlAux = window.location.href.replace('/i/', '/c/');
      window.location.assign(urlAux);
    }
  }, [finishedPosting]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        phoneInputWrapperRef.current &&
        !phoneInputWrapperRef.current.contains(event.target as Node)
      ) {
        setTouched((prev) => ({ ...prev, phone: true }));
        setIsPhoneFocused(false);
      }
    };

    if (isPhoneFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPhoneFocused]);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !isBlockedDomain;

  const showEmailError =
    email === '' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || isBlockedDomain;

  const phoneErrorList = [
    {
      condition: phoneNumber !== '' && !/^[-+()\d\s]+$/.test(phoneNumber),
      message: 'Invalid phone number',
    },
    {
      condition:
        phoneNumber !== '' &&
        countryCode === '+1' &&
        !/^(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(phoneNumber),
      message: 'Invalid US phone number',
    },
  ];

  const phoneError = phoneErrorList.find((item) => item.condition);

  const uniqueCountryCodes = countryCodes.reduce((acc, current) => {
    if (!acc.find((item) => item.code === current.code)) {
      acc.push(current);
    }
    return acc;
  }, [] as typeof countryCodes);

  const countryOptions = [
    { code: '+1', label: 'United States' },
    ...uniqueCountryCodes
      .filter((item) => item.code !== '+1')
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const selectedOption = countryOptions.find((option) => option.code === countryCode) || null;

  const isDisabled =
    !firstName ||
    !lastName ||
    !email ||
    !phoneNumber ||
    !countryCode ||
    !isValidEmail ||
    !!phoneError;

  const finishContactInfo = () => {
    setShowPreferredEmailRejected(false);
    handleSubmit(
      {
        id: surveyContent?.subject ?? '',
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: `${countryCode}${phoneNumber}`,
      },
      setShowAlert,
      setFinishedPosting,
      () => setShowPreferredEmailRejected(true),
    );
  };

  return (
    <Box
      component="form"
      autoComplete="off"
      sx={{ px: 2, py: 0, width: '100%', maxWidth: 500, mx: 'auto' }}
    >
      <Card sx={{ width: '100%', borderRadius: '16px' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Alert
            severity="success"
            icon={<CelebrationOutlinedIcon sx={{ fontSize: '20px' }} />}
            sx={{
              bgcolor: '#e6f5eb',
              border: '1px solid #c3e5cd',
              color: '#065b2c',
              py: '0px !important',
              width: 'fit-content',
              mt: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#065b2c', fontSize: '11px' }}>
              You’re Almost there!
            </Typography>
          </Alert>

          {showPreferredEmailRejected && (
            <Box sx={{ width: '100%' }}>
              <Alert
                severity="warning"
                onClose={() => setShowPreferredEmailRejected(false)}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" component="span">
                  We could not use this email address for your account. Please enter a different email
                  and submit again.
                </Typography>
              </Alert>
            </Box>
          )}

          {showAlert && (
            <Box sx={{ position: 'fixed', top: '16px', zIndex: 999999 }}>
              <AlertComp
                severity="error"
                open={showAlert}
                onClose={() => setShowAlert(false)}
                title="Sorry, there was an error submitting your contact information"
                message="Please try again."
              />
            </Box>
          )}

          <Box>
            <Typography variant="h3" fontWeight="bold" marginBottom="16px">
              Contact Information
            </Typography>
            <Typography variant="body2" sx={{ color: '#384151' }}>
              Please provide your preferred contact information details. We will use these to send
              your consultation invitation if selected
            </Typography>
          </Box>

          <TextField
            label="First Name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
            fullWidth
            autoComplete="off"
            error={touched.firstName && firstName === ''}
            helperText={touched.firstName && firstName === '' ? 'Required field' : ''}
          />

          <TextField
            label="Last Name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
            fullWidth
            autoComplete="off"
            error={touched.lastName && lastName === ''}
            helperText={touched.lastName && lastName === '' ? 'Required field' : ''}
          />

          <TextField
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            fullWidth
            autoComplete="off"
            error={touched.email && showEmailError}
            helperText={touched.email && showEmailError ? 'Enter a valid email address' : ''}
          />

          <Box>
            <Box
              ref={phoneInputWrapperRef}
              sx={{
                display: 'flex',
                width: '100%',
                minWidth: 0,
                border: '1px solid',
                borderColor: touched.phone && phoneError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                borderRadius: '8px',
                overflow: 'hidden',
                '&:focus-within': {
                  borderColor: touched.phone && phoneError ? 'error.main' : 'primary.main',
                  borderWidth: '2px',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: '36%', sm: 150 },
                  minWidth: { xs: 92, sm: 120 },
                  maxWidth: { xs: 120, sm: 170 },
                }}
              >
                <Autocomplete
                  value={selectedOption}
                  options={countryOptions}
                  getOptionLabel={(option) => option.code}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  openOnFocus
                  autoHighlight
                  blurOnSelect
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) {
                      return options;
                    }

                    const rawSearch = inputValue.toLowerCase().trim();
                    const normalizedSearch = rawSearch.replace(/\+/g, '');
                    const isNumericSearch = /^\d+$/.test(normalizedSearch);

                    return options.filter((option) => {
                      const normalizedCode = option.code.toLowerCase().replace(/\+/g, '');
                      const normalizedLabel = option.label.toLowerCase();

                      if (isNumericSearch) {
                        return normalizedCode.startsWith(normalizedSearch);
                      }

                      return normalizedLabel.includes(rawSearch);
                    });
                  }}
                  onChange={(_, newValue) => {
                    setCountryCode(newValue?.code || '');
                    setTouched((prev) => ({ ...prev, phone: false }));
                  }}
                  onOpen={() => setIsPhoneFocused(true)}
                  onClose={() => {
                    setTouched((prev) => ({ ...prev, phone: true }));
                    setIsPhoneFocused(false);
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.code} ({option.label})
                    </li>
                  )}
                  slotProps={{
                    paper: { sx: { minWidth: 320 } },
                    popper: {
                      sx: {
                        '& .MuiAutocomplete-paper': { minWidth: 320 },
                        '& .MuiAutocomplete-listbox': { fontSize: '16px' },
                      },
                    },
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      border: 'none',
                      borderRadius: 0,
                      height: '56px',
                      paddingRight: '8px !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiInputBase-input': {
                      color: '#48494d',
                      fontSize: '16px',
                      minWidth: 0,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="+1 ..."
                      autoComplete="off"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off',
                        name: 'country-code-search',
                      }}
                      onFocus={() => setIsPhoneFocused(true)}
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: '16.5px 14px',
                          color: '#48494d',
                          WebkitTextFillColor: '#48494d',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#48494d',
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  width: '1px',
                  flexShrink: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Phone number *"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  onFocus={() => setIsPhoneFocused(true)}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, phone: true }));
                    setIsPhoneFocused(false);
                  }}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      border: 'none',
                      borderRadius: 0,
                      height: '56px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiInputBase-input': {
                      color: '#111827',
                      WebkitTextFillColor: '#111827',
                      fontSize: '16px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#111827',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            {touched.phone && phoneError && (
              <Typography variant="caption" color="error" sx={{ ml: 1.75, mt: 0.75 }}>
                {phoneError.message}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          mt: 2,
          px: 2,
          py: 1,
          backgroundColor: '#eef6ff',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          border: '1px solid #c6d9ec',
        }}
      >
        <AdminPanelSettingsOutlinedIcon />
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            Your Privacy Matters
          </Typography>
          <Typography variant="body2" sx={{ color: '#384151' }}>
            We take your privacy seriously. Your contact information will never be shared with third
            parties.
          </Typography>
          <Typography
            variant="body2"
            component={Link}
            to={ROUTES.SURVEY.PATHS.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#005FCC', textDecoration: 'underline', fontWeight: 500 }}
          >
            Privacy Policy
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        color="primary"
        disabled={isDisabled}
        fullWidth
        onClick={finishContactInfo}
        sx={{ mt: 3, borderRadius: '8px' }}
      >
        SUBMIT
      </Button>
    </Box>
  );
};

export default ContactInformationForm;
